<template>
  <div>
    <andes-header></andes-header>
    <breadcrumb v-if="!ancillaryFromItinerary" activePage="3"></breadcrumb>
    <div class="container seats-page">
      <div class="row">
        <div class="col-sm-9">
          <h3 class="seats-title">{{ ancillaryFromItinerary ? $t('Comprar asiento') : $t('Selección de asientos') }}</h3>
          <p class="extras-intro" v-if="ancillaryFromItinerary">
            {{ $t('Reserva') }} {{ booking.RecordLocator }}. {{ $t('Elegí un asiento por pasajero y tramo.') }}
          </p>
          <p class="extras-intro" v-else>
            {{ $t('Elegí un asiento por pasajero y tramo. Si preferís, podés elegir el asiento más tarde en el check-in.') }}
          </p>

          <div class="seats-segment-tabs" v-if="segmentTabs.length > 1">
            <button
              type="button"
              class="btn btn-sm"
              :class="activeSegmentKey === tab.key ? 'btn-danger' : 'btn-outline-secondary'"
              v-for="tab in segmentTabs"
              :key="tab.key"
              @click="selectSegmentTab(tab)"
            >
              {{ tab.label }}
            </button>
          </div>

          <div class="row" v-if="currentPax">
            <div class="col-12 col-md-7">
              <seatmap-plane
                :seat-map="seatMapDictionary"
                :prices="prices"
                :sales-mode="true"
                :current-pax="currentPax"
                @assignSeat="assignSeat"
              ></seatmap-plane>
            </div>
            <div class="col-12 col-md-5 seat-info-box">
              <seat-select-passenger
                class="mt-3"
                :paxs="paxsForActiveSegment"
                :current-pax="currentPax"
                :prices="prices"
                :route-label="activeRouteLabel"
                @select-pax="onSelectPax"
                @clear-seat="clearSeat"
              ></seat-select-passenger>
              <div class="seat-selected-price mt-3" v-if="selectedPriceInfo || currentSeatLabel">
                <strong>{{ $t('Asiento') }} {{ currentSeatLabel }}</strong>
                <template v-if="selectedPriceInfo">
                  <div>{{ selectedPriceInfo.ZoneName || selectedPriceInfo.ZoneCode }}</div>
                  <div v-if="seatUpgradeDue > 0">
                    {{ $t('Diferencia a pagar:') }}
                    {{ selectedPriceInfo.CurrencyId || 'ARS' }}
                    {{ formatAmount(seatUpgradeDue) }}
                  </div>
                  <div v-else>
                    {{ selectedPriceInfo.CurrencyId || 'ARS' }}
                    {{ formatAmount(selectedPriceInfo.Amount) }}
                  </div>
                </template>
                <button
                  v-if="canClearCurrent"
                  type="button"
                  class="btn btn-outline-danger btn-sm btn-block mt-2"
                  :disabled="saving"
                  @click="clearSeat(currentPax)"
                >
                  {{ $t('Cancelar asiento') }}
                </button>
              </div>
              <hr>
              <seatmap-references/>
              <div class="row mt-3">
                <div class="col-12">
                  <button type="button" class="btn btn-link btn-block" @click="onSkipSeats">
                    {{ ancillaryFromItinerary ? $t('Volver al itinerario') : $t('Elegir asiento más tarde') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-sm-3">
          <summary-form v-if="!ancillaryFromItinerary" :onContinue="onContinue"></summary-form>
          <div v-else class="ancillary-summary sticky-top pt-2">
            <div class="ancillary-summary-card">
              <h4>{{ $t('Tu reserva') }}</h4>
              <div class="ancillary-summary-row">
                <span>{{ $t('Total') }}</span>
                <strong>ARS {{formatAmount(booking.TotalCost)}}</strong>
              </div>
              <div class="ancillary-summary-row" v-if="balanceDue > 0">
                <span>{{ $t('Saldo a pagar') }}</span>
                <strong class="ancillary-summary-due">ARS {{formatAmount(balanceDue)}}</strong>
              </div>
              <button class="btn btn-andes btn-block mt-3" type="button" @click="onContinue">{{ $t('Continuar') }}</button>
              <button type="button" class="btn btn-link btn-block ancillary-summary-back" @click="onSkipSeats">
                {{ $t('Volver al itinerario') }}
              </button>
            </div>
          </div>
          <button v-if="!ancillaryFromItinerary" type="button" class="btn btn-link btn-block mt-2 seats-skip-side" @click="onSkipSeats">
            {{ $t('Elegir asiento más tarde') }}
          </button>
        </div>
      </div>
    </div>
    <andes-footer></andes-footer>
    <loading-modal v-if="loading"></loading-modal>
  </div>
</template>

<script>
  import AndesHeader from "../components/AndesHeader";
  import AndesFooter from "../components/AndesFooter";
  import Breadcrumb from "../components/Breadcrumb";
  import SummaryForm from "../components/SummaryForm";
  import LoadingModal from "../components/LoadingModal";
  import SeatOverlayInformation from "../components/seatmap/SeatOverlayInformation";
  import SeatSelectPassenger from "../components/seatmap/SeatSelectPassenger";
  import SeatmapPlane from "../components/seatmap/Seatmap";
  import SeatmapReferences from "../components/seatmap/SeatmapReferences";
  import { getSeatMap, getSeatPrices, setBookingSeat, skipBookingSeats } from '@/api/api';
  import { mapState } from 'vuex';
  import { bookingHasCharterFlight, formatMoney } from '@/utils/utils';

  export default {
    name: "SeatsPage",
    components: {
      SeatmapReferences,
      SeatmapPlane,
      SeatSelectPassenger,
      SeatOverlayInformation,
      LoadingModal,
      AndesFooter,
      AndesHeader,
      Breadcrumb,
      SummaryForm,
    },
    computed: {
      ...mapState(['booking', 'loading', 'ancillaryFromItinerary']),
      balanceDue: function () {
        return parseFloat(this.booking.BalanceDue || 0);
      },
      segmentTabs: function () {
        if (!this.booking || !this.booking.Passengers || !this.booking.Passengers.length) {
          return [];
        }
        let tabs = [];
        let seen = {};
        for (let pax of this.booking.Passengers) {
          for (let seg of pax.Segments || []) {
            let key = seg.DepartureStation + '-' + seg.ArrivalStation + '-' + (seg.DepartureDate || '');
            if (seen[key]) continue;
            seen[key] = true;
            tabs.push({
              key: key,
              label: seg.DepartureStation + ' → ' + seg.ArrivalStation,
              departureStation: seg.DepartureStation,
              arrivalStation: seg.ArrivalStation,
              departureDate: seg.DepartureDate,
            });
          }
        }
        return tabs;
      },
      paxsForActiveSegment: function () {
        let list = [];
        if (!this.booking || !this.booking.Passengers) return list;
        for (let pax of this.booking.Passengers) {
          for (let seg of pax.Segments || []) {
            if (!this.matchesActiveSegment(seg)) continue;
            list.push({
              pax: pax,
              segId: seg.SegmentID,
              segment: seg,
              paxLeg: seg.Legs[0],
            });
          }
        }
        return list;
      },
      selectedPriceInfo: function () {
        if (!this.currentPax || !this.currentPax.paxLeg || !this.currentPax.paxLeg.UnitDesignator) {
          return null;
        }
        return this.prices[this.currentPax.paxLeg.UnitDesignator] || null;
      },
      currentSeatLabel: function () {
        return this.currentPax && this.currentPax.paxLeg && this.currentPax.paxLeg.UnitDesignator
          ? this.currentPax.paxLeg.UnitDesignator
          : null;
      },
      seatUpgradeDue: function () {
        if (!this.currentPax || !this.currentPax.segment || !this.currentPax.segment.Fees) return 0;
        let total = 0;
        for (let fee of this.currentPax.segment.Fees) {
          if (!this.isSeatFee(fee) || fee.PaymentID) continue;
          for (let charge of fee.Charges || []) {
            if (charge.ChargeType == 3) continue;
            let k = charge.ChargeType == 1 ? -1 : 1;
            total += parseFloat(charge.ChargeAmount || 0) * k;
          }
        }
        return total;
      },
      canClearCurrent: function () {
        if (!this.currentSeatLabel) return false;
        let fees = this.currentPax.segment && this.currentPax.segment.Fees;
        if (!fees || !fees.length) return true;
        let hasUnpaid = fees.some((fee) => this.isSeatFee(fee) && !fee.PaymentID);
        if (hasUnpaid) return true;
        let hasPaid = fees.some((fee) => this.isSeatFee(fee) && fee.PaymentID);
        return !hasPaid;
      },
      activeRouteLabel: function () {
        let tab = _.find(this.segmentTabs, (item) => item.key === this.activeSegmentKey);
        return tab ? tab.label : '';
      },
    },
    data: function () {
      return {
        seatMapDictionary: {},
        prices: {},
        currentPax: null,
        activeSegmentKey: null,
        saving: false,
      };
    },
    methods: {
      isSeatFee: function (fee) {
        return Number(fee.FeeType) === 9 || (fee.Fee && Number(fee.Fee.FeeType) === 9)
          || fee.FeeCode === 'SEAT' || (fee.Fee && fee.Fee.FeeCode === 'SEAT');
      },
      formatAmount: function (amount) {
        return formatMoney(amount);
      },
      matchesActiveSegment: function (seg) {
        if (!this.activeSegmentKey) return true;
        let key = seg.DepartureStation + '-' + seg.ArrivalStation + '-' + (seg.DepartureDate || '');
        return key === this.activeSegmentKey;
      },
      selectSegmentTab: function (tab) {
        this.activeSegmentKey = tab.key;
        this.reloadSeatData();
      },
      syncCurrentPax: function () {
        let list = this.paxsForActiveSegment;
        if (!list.length) {
          this.currentPax = null;
          return;
        }
        if (this.currentPax) {
          let found = _.find(list, (item) =>
            item.pax.PassengerID === this.currentPax.pax.PassengerID && item.segId === this.currentPax.segId
          );
          if (found) {
            this.currentPax = found;
            return;
          }
        }
        this.currentPax = list[0];
      },
      reloadSeatData: function () {
        this.syncCurrentPax();
        if (!this.currentPax) return;
        let legIds = [];
        for (let leg of this.currentPax.segment.Legs || []) {
          legIds.push(leg.InventoryLegID);
        }
        this.$store.commit('loading', true);
        getSeatMap(legIds, (seatMapDictionary) => {
          this.seatMapDictionary = seatMapDictionary || {};
          getSeatPrices(this.booking.BookingID, this.currentPax.segId, (result) => {
            this.prices = (result && result.Prices) || {};
            this.$store.commit('loading', false);
          }, (error) => {
            this.prices = {};
            this.$store.commit('loading', false);
            alert(error);
          });
        });
      },
      assignSeat: function (pax, seat) {
        if (!pax || this.saving) return;
        this.saving = true;
        this.$store.commit('loading', true);
        setBookingSeat({
          BookingID: this.booking.BookingID,
          PassengerID: pax.pax.PassengerID,
          SegmentID: pax.segId,
          UnitDesignator: seat,
        }, (result) => {
          this.$store.dispatch('updateBooking', result.Booking || result);
          if (result.SeatMap) {
            this.seatMapDictionary = result.SeatMap;
          }
          this.saving = false;
          this.$store.commit('loading', false);
          this.$nextTick(() => {
            this.syncCurrentPax();
            this.nextPassenger();
          });
        }, (error) => {
          this.saving = false;
          this.$store.commit('loading', false);
          alert(error);
        });
      },
      clearSeat: function (pax) {
        if (!pax || this.saving) return;
        if (!pax.paxLeg || !pax.paxLeg.UnitDesignator) return;
        this.saving = true;
        this.$store.commit('loading', true);
        setBookingSeat({
          BookingID: this.booking.BookingID,
          PassengerID: pax.pax.PassengerID,
          SegmentID: pax.segId,
          UnitDesignator: null,
        }, (result) => {
          this.$store.dispatch('updateBooking', result.Booking || result);
          if (result.SeatMap) {
            this.seatMapDictionary = result.SeatMap;
          }
          this.saving = false;
          this.$store.commit('loading', false);
          this.$nextTick(() => {
            this.syncCurrentPax();
          });
        }, (error) => {
          this.saving = false;
          this.$store.commit('loading', false);
          alert(error);
        });
      },
      nextPassenger: function () {
        let list = this.paxsForActiveSegment;
        let index = _.findIndex(list, (item) =>
          item.pax.PassengerID === this.currentPax.pax.PassengerID && item.segId === this.currentPax.segId
        );
        if (index >= 0 && index < list.length - 1) {
          this.currentPax = list[index + 1];
        }
      },
      onSelectPax: function (value) {
        this.currentPax = value;
      },
      onContinue: function () {
        if (this.ancillaryFromItinerary) {
          this.goAfterAncillary();
          return;
        }
        this.$router.push('payment');
      },
      goAfterAncillary: function () {
        if (this.balanceDue > 0) {
          this.$store.commit('afterPaymentRoute', 'itinerary');
          this.$router.push('payment-pending');
        } else {
          this.$store.commit('ancillaryFromItinerary', false);
          this.$router.replace('itinerary');
        }
      },
      onSkipSeats: function () {
        if (this.ancillaryFromItinerary) {
          this.$store.commit('ancillaryFromItinerary', false);
          this.$router.replace('itinerary');
          return;
        }
        this.$store.commit('loading', true);
        skipBookingSeats(this.booking.BookingID, (result) => {
          this.$store.dispatch('updateBooking', result);
          this.$store.commit('loading', false);
          this.$router.push('payment');
        }, (error) => {
          this.$store.commit('loading', false);
          alert(error);
        });
      },
    },
    mounted() {
      if (_.isEmpty(this.booking)) {
        this.$router.replace('/');
        return;
      }
      if (this.ancillaryFromItinerary && bookingHasCharterFlight(this.booking)) {
        this.$store.commit('ancillaryFromItinerary', false);
        this.$router.replace('itinerary');
        return;
      }
      if (this.segmentTabs.length) {
        this.activeSegmentKey = this.segmentTabs[0].key;
      }
      this.reloadSeatData();
    },
    watch: {
      booking: function () {
        this.syncCurrentPax();
      },
    },
  }
</script>

<style scoped>
.seats-title {
  color: #AA1E1E;
  margin-bottom: 8px;
}
.seats-segment-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.seat-selected-price {
  background: #fafafa;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 12px 14px;
}
.seats-skip-side {
  color: #666;
  font-size: 14px;
}
.ancillary-summary-card {
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  padding: 18px 16px 14px;
}
.ancillary-summary-card h4 {
  margin: 0 0 16px;
  color: #333;
  font-size: 18px;
  font-weight: 700;
}
.ancillary-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
  color: #555;
  font-size: 14px;
}
.ancillary-summary-row strong {
  color: #222;
  font-size: 15px;
  white-space: nowrap;
}
.ancillary-summary-due {
  color: #cc353a !important;
}
.ancillary-summary-back {
  color: #666;
  margin-top: 4px;
}
</style>
